import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Grid,
  Pagination,
  Divider,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  Campaign as CampaignIcon,
  Article as ArticleIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/Layout/MainLayout';
import { useTimeline } from '../../hooks/useTimeline';
import { SponsorCarousel } from '../../components/Timeline/SponsorCarousel';
import {
  TimelinePost,
  PostCategory,
  POST_CATEGORY_LABELS,
  POST_CATEGORY_COLORS,
} from '../../types/timeline';
import { SponsorCompany } from '../../services/sponsorService';
import axios from 'axios';
import { LinkifiedText } from '../../components/Common/LinkifiedText';

// カテゴリアイコンマッピング
const CATEGORY_ICONS: Record<PostCategory, React.ReactElement> = {
  [PostCategory.RECRUITMENT]: <CampaignIcon />,
  [PostCategory.EVENT]: <EventIcon />,
  [PostCategory.COMPANY_NEWS]: <ArticleIcon />,
  [PostCategory.OTHER]: <BusinessIcon />,
};

// 相対時間フォーマット
const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return '数秒前';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}分前`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}時間前`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}日前`;

  return date.toLocaleDateString('ja-JP');
};

const TimelineViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { posts, loading, pagination, toggleLike, loadPosts, loadPage } =
    useTimeline();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<PostCategory[]>(
    []
  );
  const [error, setError] = useState<string | null>(null);
  const [viewedPosts, setViewedPosts] = useState<Set<number>>(new Set());

  // 初期データ読み込み
  useEffect(() => {
    let mounted = true;

    loadPosts().catch((err) => {
      if (mounted) {
        setError('投稿の読み込みに失敗しました');
        console.error(err);
      }
    });

    return () => {
      mounted = false;
    };
  }, [loadPosts]);

  const handleCategoryFilter = (categories: PostCategory[]) => {
    setSelectedCategories(categories);
    const query = categories.length > 0 ? { categories } : {};
    loadPosts(query).catch((err) => {
      setError('投稿の読み込みに失敗しました');
      console.error(err);
    });
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    const query =
      selectedCategories.length > 0 ? { categories: selectedCategories } : {};
    loadPage(page, query).catch((err) => {
      setError('ページの読み込みに失敗しました');
      console.error(err);
    });
  };

  const refreshTimeline = () => {
    setError(null);
    const query =
      selectedCategories.length > 0 ? { categories: selectedCategories } : {};
    loadPosts(query).catch((err) => {
      setError('投稿の読み込みに失敗しました');
      console.error(err);
    });
  };

  const handleLike = async (postId: number) => {
    await toggleLike(postId);
  };

  // スポンサー企業クリック時の処理
  const handleSponsorCompanyClick = (company: SponsorCompany) => {
    navigate(`/companies/${company.id}/profile`, {
      state: { from: 'timeline' },
    });
  };

  // 表示回数カウント（バッチ処理）
  const incrementViewCountBatch = useCallback(async (postIds: number[]) => {
    if (postIds.length === 0) return;

    try {
      await axios.post('/api/timeline/posts/view-batch', {
        postIds: postIds,
      });

      // 閲覧済みとしてマーク
      setViewedPosts((prev) => {
        const newSet = new Set(prev);
        postIds.forEach((id) => newSet.add(id));
        return newSet;
      });
    } catch (error) {
      console.error('Failed to increment view count batch:', error);
    }
  }, []);

  // 企業プロフィールページに遷移
  const handleCompanyClick = (companyId: number) => {
    navigate(`/companies/${companyId}/profile`, {
      state: { from: 'timeline' },
    });
  };

  // 投稿が表示された時に表示回数をカウント（バッチ処理）
  useEffect(() => {
    const unviewedPostIds = posts
      .filter((post) => !viewedPosts.has(post.id))
      .map((post) => post.id);

    if (unviewedPostIds.length > 0) {
      incrementViewCountBatch(unviewedPostIds);
    }
  }, [posts, viewedPosts, incrementViewCountBatch]);

  const renderPostCard = (post: TimelinePost) => (
    <Card key={post.id} sx={{ mb: 3, boxShadow: 2 }}>
      <CardContent>
        {/* 企業情報 */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            src={post.company?.logo_url || undefined}
            sx={{ width: 48, height: 48, mr: 2, bgcolor: 'grey.300' }}
          >
            <BusinessIcon />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Link
              component="button"
              variant="h6"
              sx={{
                fontWeight: 600,
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': {
                  color: 'primary.main',
                  textDecoration: 'underline',
                },
              }}
              onClick={() =>
                post.company?.id && handleCompanyClick(post.company.id)
              }
            >
              {post.company?.name || '企業名不明'}
            </Link>
            <Typography variant="body2" color="text.secondary">
              {post.author?.full_name || post.author?.name}
              {post.author?.title && ` • ${post.author.title}`}
              {' • '}
              {post.published_at
                ? formatRelativeTime(new Date(post.published_at))
                : ''}
            </Typography>
          </Box>
          <Chip
            icon={CATEGORY_ICONS[post.category]}
            label={POST_CATEGORY_LABELS[post.category]}
            size="small"
            sx={{
              bgcolor: POST_CATEGORY_COLORS[post.category],
              color: 'white',
              '& .MuiChip-icon': { color: 'white' },
            }}
          />
        </Box>

        {/* 投稿内容 */}
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          {post.title}
        </Typography>

        <LinkifiedText
          text={post.content}
          variant="body1"
          sx={{ mb: 3, lineHeight: 1.7 }}
        />

        {/* 画像表示 */}
        {post.image_url && (
          <Box sx={{ mb: 2 }}>
            <img
              src={post.image_url}
              alt={post.title}
              style={{
                width: '100%',
                maxHeight: 400,
                objectFit: 'cover',
                borderRadius: 8,
                display: 'block',
              }}
            />
          </Box>
        )}

        {/* 公開期間 */}
        {post.publication_end && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label={`公開期限: ${new Date(post.publication_end).toLocaleDateString('ja-JP')}`}
              size="small"
              variant="outlined"
              color="warning"
            />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* エンゲージメント */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => handleLike(post.id)}
              color={post.is_liked_by_user ? 'error' : 'default'}
              sx={{
                '&:hover': {
                  bgcolor: post.is_liked_by_user ? 'error.light' : 'grey.100',
                },
              }}
            >
              {post.is_liked_by_user ? (
                <FavoriteIcon />
              ) : (
                <FavoriteBorderIcon />
              )}
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              {post.like_count} いいね
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary">
            {post.view_count} 回表示
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <MainLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* ヘッダー */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
            タイムライン
          </Typography>
          <Typography variant="body1" color="text.secondary">
            企業からのお知らせや採用情報をチェックしましょう
          </Typography>
        </Box>

        {/* スポンサーカルーセル */}
        <SponsorCarousel onCompanyClick={handleSponsorCompanyClick} />

        {/* フィルターとアクション */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant={showFilters ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            フィルター
          </Button>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshTimeline}
            disabled={loading}
          >
            更新
          </Button>
        </Box>

        {/* フィルターUI */}
        {showFilters && (
          <Card sx={{ mb: 3, p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              カテゴリフィルター
            </Typography>
            <FormControl fullWidth>
              <InputLabel>カテゴリを選択</InputLabel>
              <Select
                multiple
                value={selectedCategories}
                onChange={(e) =>
                  handleCategoryFilter(e.target.value as PostCategory[])
                }
                input={<OutlinedInput label="カテゴリを選択" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as PostCategory[]).map((value) => (
                      <Chip
                        key={value}
                        label={POST_CATEGORY_LABELS[value]}
                        size="small"
                        sx={{
                          bgcolor: POST_CATEGORY_COLORS[value],
                          color: 'white',
                        }}
                      />
                    ))}
                  </Box>
                )}
              >
                {Object.values(PostCategory).map((category) => (
                  <MenuItem key={category} value={category}>
                    <Checkbox checked={selectedCategories.includes(category)} />
                    <ListItemText primary={POST_CATEGORY_LABELS[category]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Card>
        )}

        {/* エラー表示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* ローディング */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* 投稿一覧 */}
        {!loading && posts.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              投稿がありません
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              フィルターを変更するか、しばらく時間をおいてから確認してください
            </Typography>
          </Card>
        ) : (
          <>
            {posts.map((post) => (
              <React.Fragment key={post.id}>
                {renderPostCard(post)}
              </React.Fragment>
            ))}

            {/* ページネーション */}
            {pagination.total_pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={pagination.total_pages}
                  page={pagination.current_page}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}

        {/* 投稿数表示 */}
        {!loading && posts.length > 0 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              全 {pagination.total_count} 件中{' '}
              {(pagination.current_page - 1) * pagination.per_page + 1} -
              {Math.min(
                pagination.current_page * pagination.per_page,
                pagination.total_count
              )}{' '}
              件を表示
            </Typography>
          </Box>
        )}
      </Container>
    </MainLayout>
  );
};

export default TimelineViewPage;
